// ============================================================
// AppointmentFormModal.tsx
// Create / edit / VIEW a calendar APPOINTMENT (meeting, court, call, other).
// "Hero-band" design: a shared green gradient header (light + dark) carries the
// type pill + title + (view) date/time/location, on BOTH the read-only VIEW and
// the EDIT/create form.
//
//   • EXISTING appointment (mode='edit') → opens in read-only VIEW first
//     (details + notes + attendees list, with a "View all N attendees" popup for
//     big meetings). Everyone can SEE the attendees (read-only).
//   • NEW appointment (mode='create') → opens straight into the EDIT form.
//   • "Edit" in the view → the EDIT form (same hero band, title becomes an input).
//
// The EDIT form keeps EVERY field/behaviour: title, date, type, start/end,
// location, linked case (+ click-through), Repeats/Ends recurrence, assignee,
// the audience control (Just me / Specific people / Everyone — permission-gated,
// creator pre-ticked) + people-picker, notes, delete, and the recurring
// occurrence-vs-series this/all dialog. Saving calls the appointment API +
// onSaved; the entity-changed broadcast makes other calendars live-update.
// ============================================================
import { BTN_PRIMARY_MD } from '../../styles/buttons'
import LastEditedLine from '../shared/LastEditedLine'
import DateInput from '../shared/DateInput'
import { ErrorNote } from '../shared/ActionBox'
import React, { useEffect, useState } from 'react'
import { useLoadPhase, SKELETON_BAR } from '../../hooks/useLoadPhase'
import { caseShortId } from '../../utils/caseName'
import {
  getAppointmentApi,
  createAppointmentApi,
  updateAppointmentApi,
  deleteAppointmentApi,
} from '../../api/casevoApi'
import CaseLink, { LockIcon, LOCKED_CASE_TIP } from '../shared/CaseLink'
import { useCaseAccess, primeCaseAccess } from '../../hooks/useCaseAccess'
import { AppointmentType, RecurrenceFreq, Weekday, Appointment } from '../../types/calendar.types'
import { FirmUser } from '../../types/user.types'
import { useCasevoStore } from '../../store/useCasevoStore'
import { useDraftDirty, useUnsavedChangesGuard } from '../../hooks/useUnsavedChangesGuard'
import LinkCasePickerModal from '../shared/LinkCasePickerModal'
import UserAvatar from '../shared/UserAvatar'
import {
  WEEKDAYS,
  weekdayCodeForDate,
  buildRecurrenceRule,
  recurrenceEndAtFor,
  parseRecurrenceRule,
} from '../../utils/recurrenceRule'

interface AppointmentFormModalProps {
  mode: 'create' | 'edit'
  appointmentId?: string
  seedDate?: string       // 'YYYY-MM-DD' (create)
  seedStartTime?: string  // 'HH:mm' (create)
  firmUsers: FirmUser[]
  onClose: () => void
  onSaved: () => void
  // Open the linked case from a saved appointment (click-through). The
  // parent handles nav + Back-origin (returns to the calendar).
  onOpenCase: (caseId: string) => void
}

const TYPES: { value: AppointmentType; label: string }[] = [
  { value: 'meeting', label: 'Meeting' },
  { value: 'court', label: 'Court hearing' },
  { value: 'call', label: 'Call' },
  { value: 'other', label: 'Other' },
]
const TYPE_LABEL: Record<AppointmentType, string> = {
  meeting: 'Meeting', court: 'Court', call: 'Call', other: 'Appointment',
}

// ── Inline line icons (Feather-style, currentColor) — no emojis, no lib ──
const IcBase: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
    className={className ?? 'w-4 h-4 inline-block align-[-3px] shrink-0'} aria-hidden="true">{children}</svg>
)
const IcCalendar: React.FC<{ className?: string }> = (p) => <IcBase {...p}><rect x="3" y="4" width="18" height="18" rx="2" /><path d="M16 2v4M8 2v4M3 10h18" /></IcBase>
const IcClock: React.FC<{ className?: string }> = (p) => <IcBase {...p}><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></IcBase>
const IcPin: React.FC<{ className?: string }> = (p) => <IcBase {...p}><path d="M21 10c0 7-9 12-9 12s-9-5-9-12a9 9 0 0 1 18 0z" /><circle cx="12" cy="10" r="3" /></IcBase>
const IcFile: React.FC<{ className?: string }> = (p) => <IcBase {...p}><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><path d="M14 2v6h6" /></IcBase>
const IcNote: React.FC<{ className?: string }> = (p) => <IcBase {...p}><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" /></IcBase>
const IcUsers: React.FC<{ className?: string }> = (p) => <IcBase {...p}><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" /></IcBase>
const IcGlobe: React.FC<{ className?: string }> = (p) => <IcBase {...p}><circle cx="12" cy="12" r="9" /><path d="M3 12h18M12 3a15 15 0 0 1 0 18M12 3a15 15 0 0 0 0 18" /></IcBase>
const IcPhone: React.FC<{ className?: string }> = (p) => <IcBase {...p}><path d="M22 16.9v3a2 2 0 0 1-2.2 2 19.8 19.8 0 0 1-8.6-3 19.5 19.5 0 0 1-6-6 19.8 19.8 0 0 1-3-8.6A2 2 0 0 1 4.1 2h3a2 2 0 0 1 2 1.7c.1 1 .4 2 .8 2.9a2 2 0 0 1-.5 2.1L8.1 9.9a16 16 0 0 0 6 6l1.2-1.3a2 2 0 0 1 2.1-.5c1 .3 1.9.6 2.9.8a2 2 0 0 1 1.7 2z" /></IcBase>
const IcBuilding: React.FC<{ className?: string }> = (p) => <IcBase {...p}><path d="M3 21h18M6 21V7l6-4 6 4v14M9 9h.01M15 9h.01M9 13h.01M15 13h.01M9 17h.01M15 17h.01" /></IcBase>
const IcCheck: React.FC = () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" className="w-3 h-3" aria-hidden="true"><path d="M20 6L9 17l-5-5" /></svg>

const typeIcon = (t: AppointmentType): React.ReactNode => {
  if (t === 'call') return <IcPhone />
  if (t === 'court') return <IcBuilding />
  return <IcCalendar />
}

// Build a full ISO instant from a 'YYYY-MM-DD' + 'HH:mm' using LOCAL parts.
const combineISO = (date: string, time: string): string | null => {
  if (!date || !time) return null
  const [y, mo, d] = date.split('-').map(Number)
  const [hh, mm] = time.split(':').map(Number)
  if ([y, mo, d, hh, mm].some((n) => Number.isNaN(n))) return null
  return new Date(y, mo - 1, d, hh, mm).toISOString()
}
const isoToDate = (iso: string): string => {
  const d = new Date(iso)
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}
const isoToTime = (iso: string): string => {
  const d = new Date(iso)
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`
}
// View-mode display formatters.
const fmtDate = (iso: string): string => new Date(iso).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })
const fmtTime = (iso: string): string => new Date(iso).toLocaleTimeString('en-GB', { hour: 'numeric', minute: '2-digit', hour12: true })
const nameOf = (u: { firstName?: string; lastName?: string; email?: string } | undefined): string =>
  u ? (`${u.firstName ?? ''} ${u.lastName ?? ''}`.trim() || u.email || 'Unknown') : 'Unknown'

const inputCls =
  'w-full border border-casevo-border rounded-lg px-3 py-2.5 text-sm bg-casevo-input dark:bg-[#1f2e26] text-casevo-forest focus:outline-none focus:border-casevo-mid'
const lblCls = 'block text-[11px] font-semibold uppercase tracking-wide text-casevo-mid mb-1.5'
const bhCls = 'flex items-center gap-2 text-[11px] font-bold uppercase tracking-wide text-casevo-mid mb-3'

// The server's message (e.g. "The end time can't be before the start."), not axios's generic one.
const serverError = (err: unknown, fallback: string): string =>
  (err as { response?: { data?: { error?: string } } })?.response?.data?.error || (err instanceof Error && err.message) || fallback

const AppointmentFormModal: React.FC<AppointmentFormModalProps> = ({
  mode, appointmentId, seedDate, seedStartTime, firmUsers, onClose, onSaved, onOpenCase,
}) => {
  const [title, setTitle] = useState('')
  const [date, setDate] = useState(seedDate ?? '')
  // (Client-sync batch) Casevo's DateInput for the date + the repeat's end
  // date (appointments can be past or future: allowFuture). A half-typed
  // date is never sent — it holds Save with a clear message.
  const [dateTyping, setDateTyping] = useState(false)
  const [endsTyping, setEndsTyping] = useState(false)
  const [startTime, setStartTime] = useState(seedStartTime ?? '09:00')
  const [endTime, setEndTime] = useState('')
  const [type, setType] = useState<AppointmentType>('meeting')
  const [location, setLocation] = useState('')
  const [notes, setNotes] = useState('')
  // Assignee (owner). CREATE mode defaults to the CURRENT USER so a "Just me"
  // appointment is owned by its creator (shows on their Mine calendar via the
  // assignee rule too); still changeable (anyone / Unassigned). Edit mode loads
  // the saved value below.
  const [assigneeId, setAssigneeId] = useState(() => (mode === 'create' ? useCasevoStore.getState().authUser?.id ?? '' : ''))
  const canInviteAttendees = useCasevoStore((s) => s.authUser?.effectivePermissions?.inviteAppointmentAttendees ?? false)
  const canCreateFirmWide = useCasevoStore((s) => s.authUser?.effectivePermissions?.createFirmWideAppointments ?? false)
  const currentUserId = useCasevoStore((s) => s.authUser?.id ?? '')
  const isAdminUser = useCasevoStore((s) => s.authUser?.role === 'admin')
  const [audience, setAudience] = useState<'me' | 'people' | 'firm'>('me')
  const [attendeeIds, setAttendeeIds] = useState<string[]>([])
  const [caseId, setCaseId] = useState<string | null>(null)
  const [clientId, setClientId] = useState<string | null>(null)
  const [casePickerOpen, setCasePickerOpen] = useState(false)
  // Locked-link UX: can the viewer open the linked case? (batched + cached)
  const caseAccessOf = useCaseAccess([caseId])
  const caseLocked = caseAccessOf(caseId) === 'locked'

  // Recurrence.
  const [freq, setFreq] = useState<RecurrenceFreq | null>(null)
  const [byday, setByday] = useState<Weekday[]>([])
  const [endsType, setEndsType] = useState<'never' | 'on' | 'after'>('never')
  const [endsDate, setEndsDate] = useState('')
  const [endsCount, setEndsCount] = useState(10)
  const isOccurrence = mode === 'edit' && !!appointmentId && appointmentId.includes(':')
  const [scopePrompt, setScopePrompt] = useState<'save' | 'delete' | null>(null)

  // The loaded appointment (for the read-only VIEW: attendees, organiser, times).
  const [loaded, setLoaded] = useState<Appointment | null>(null)
  // View vs edit. Existing appt → view first; new → straight to edit.
  const [view, setView] = useState<'view' | 'edit'>(mode === 'edit' ? 'view' : 'edit')
  const [attendeesPopup, setAttendeesPopup] = useState(false)

  const [loading, setLoading] = useState(mode === 'edit')
  const loadPhase = useLoadPhase(!loading)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // (Audit batch 3) The server sent the RESTRICTED shape: the appointment is on
  // a case this viewer can't open and they aren't involved — only when it is.
  const [restricted, setRestricted] = useState(false)

  // Edit mode: fetch the full appointment row and populate.
  useEffect(() => {
    if (mode !== 'edit' || !appointmentId) return
    let cancelled = false
    setLoading(true)
    getAppointmentApi(appointmentId)
      .then((a) => {
        if (cancelled) return
        setLoaded(a)
        setTitle(a.title)
        if (a.restricted) { setRestricted(true); return }
        setDate(isoToDate(a.startAt))
        setStartTime(isoToTime(a.startAt))
        setEndTime(a.endAt ? isoToTime(a.endAt) : '')
        setType(a.type)
        setLocation(a.location ?? '')
        setNotes(a.notes ?? '')
        setAssigneeId(a.assignedToUserId ?? '')
        if (a.firmWide) {
          setAudience('firm')
        } else if (a.attendees && a.attendees.length > 0) {
          setAudience('people')
          setAttendeeIds(a.attendees.map((at) => at.userId))
        } else {
          setAudience('me')
        }
        // ★ Seed the locked-link cache from the payload BEFORE the link first
        // renders, so it paints locked/clickable correctly from frame one.
        primeCaseAccess(a.caseId, a.caseAccessible)
        setCaseId(a.caseId)
        setClientId(a.clientId)
        const rule = parseRecurrenceRule(a.recurrenceRule)
        if (rule) {
          setFreq(rule.freq)
          setByday(rule.byday ?? [])
          setEndsType(rule.ends.type)
          if (rule.ends.type === 'on' && rule.ends.date) setEndsDate(rule.ends.date)
          if (rule.ends.type === 'after' && rule.ends.count) setEndsCount(rule.ends.count)
        }
      })
      .catch(() => { if (!cancelled) setError('Could not load this appointment.') })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [mode, appointmentId])

  const handleSave = async (scope: 'series' | 'occurrence' = 'series'): Promise<void> => {
    setScopePrompt(null)
    const t = title.trim()
    if (!t) { setError('A title is required.'); return }
    if (dateTyping || (freq && endsType === 'on' && endsTyping)) { setError('Finish the date, or clear it.'); return }
    const startAt = combineISO(date, startTime)
    if (!startAt) { setError('A valid date and start time are required.'); return }
    const endAt = endTime ? combineISO(date, endTime) : null
    // Same rules as the server (which refuses these with a 400).
    if (endTime && !endAt) { setError('Enter a valid end time, or leave it empty.'); return }
    if (endAt && new Date(endAt).getTime() < new Date(startAt).getTime()) { setError('The end time can’t be before the start.'); return }
    if (scope !== 'occurrence' && freq && endsType === 'on' && (!endsDate || endsDate < date)) { setError(endsDate ? 'The repeat can’t end before the first appointment.' : 'Choose the date the repeat ends.'); return }
    setSaving(true)
    setError(null)
    try {
      const ends = {
        type: endsType,
        date: endsType === 'on' ? endsDate : undefined,
        count: endsType === 'after' ? endsCount : undefined,
      }
      const recurrenceRule = scope === 'occurrence' ? undefined : buildRecurrenceRule(freq, byday, ends)
      const recurrenceEndAt = scope === 'occurrence' ? undefined : recurrenceRule ? recurrenceEndAtFor(ends) : null
      const payload = {
        title: t,
        startAt,
        endAt,
        type,
        location: location.trim() || null,
        notes: notes.trim() || null,
        caseId,
        clientId,
        assignedToUserId: assigneeId || null,
        // Only send the audience fields the user is PERMITTED to change (backend
        // is the real gate; keeps a non-granted attendee's time/notes edit clean).
        ...(canCreateFirmWide ? { firmWide: audience === 'firm' } : {}),
        ...(canInviteAttendees ? { attendeeUserIds: audience === 'people' ? attendeeIds : [] } : {}),
        recurrenceRule,
        recurrenceEndAt,
      }
      if (mode === 'edit' && appointmentId) {
        await updateAppointmentApi(appointmentId, payload, scope)
      } else {
        await createAppointmentApi(payload)
      }
      onSaved()
      onClose()
    } catch (err) {
      setError(serverError(err, 'Failed to save appointment'))
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async (scope: 'series' | 'occurrence' = 'series'): Promise<void> => {
    if (mode !== 'edit' || !appointmentId) return
    setScopePrompt(null)
    setSaving(true)
    setError(null)
    try {
      await deleteAppointmentApi(appointmentId, scope)
      onSaved()
      onClose()
    } catch (err) {
      setError(serverError(err, 'Failed to delete appointment'))
      setSaving(false)
    }
  }

  const onSaveClick = (): void => { if (isOccurrence) setScopePrompt('save'); else void handleSave('series') }
  const onDeleteClick = (): void => { if (isOccurrence) setScopePrompt('delete'); else void handleDelete('series') }
  // Cancel from edit: an existing appointment returns to its VIEW; a new one closes.
  // Unsaved-changes guard: the edit form differs from what it opened with →
  // ×, Cancel, browser Back, leaving the calendar or a refresh ask first.
  const draftDirty = useDraftDirty(view === 'edit' && !loading, {
    title, date, startTime, endTime, type, location, notes, assigneeId, audience, attendeeIds,
    caseId, clientId, freq, byday, endsType, endsDate, endsCount,
  })
  const guardLeave = useUnsavedChangesGuard(draftDirty && !saving)
  const requestClose = (): void => guardLeave(onClose)
  const onCancelEdit = (): void => guardLeave(() => { if (mode === 'edit') { setView('view'); setError(null) } else onClose() })

  // ── Attendee display list for the VIEW (organiser first, tagged) ──
  const userById = (id: string): FirmUser | undefined => firmUsers.find((u) => u.id === id)
  const organiserId = loaded?.createdByUserId ?? ''
  // Mirrors the server rule (appointmentController checkAppointmentModify):
  // the involved people (organiser / assignee / attendee) or an admin may edit
  // AND delete; editOthersAppointments adds edit only (below). UX-only — the
  // server enforces it (403) regardless.
  // ★ Was `!loaded || …` — meant for CREATE mode, but in VIEW mode `loaded` is
  // also null while the fetch is in flight or after it FAILS, so a user with no
  // involvement and no permission got the Edit (and Delete) buttons. Now only
  // create mode is unconditional; an existing appointment needs the loaded row.
  const isInvolvedOrAdmin = mode === 'create' || (!!loaded && (
    isAdminUser
    || loaded.createdByUserId === currentUserId
    || loaded.assignedToUserId === currentUserId
    || (loaded.attendees ?? []).some((a) => a.userId === currentUserId)
  ))
  // ★ "Edit others' appointments" (editOthersAppointments) — the backend
  // (checkAppointmentModify mode 'edit') lets the holder EDIT + MOVE any
  // appointment, but never DELETE (mode 'delete' stays involved/admin). So the
  // UI splits the old single gate the same way: canModify = edit, canDelete =
  // the original involved-people + admin rule.
  const canEditOthersAppointments = useCasevoStore((s) => s.authUser?.effectivePermissions?.editOthersAppointments ?? false)
  const canModify = isInvolvedOrAdmin || (!!loaded && canEditOthersAppointments)
  const canDelete = isInvolvedOrAdmin
  // (Each row's avatar: UserAvatar by id — the same as everywhere else.)
  const attendeeRows: { id: string; name: string; organiser: boolean }[] = (() => {
    const ids = (loaded?.attendees ?? []).map((a) => a.userId)
    const withOrg = ids.includes(organiserId) || !organiserId ? ids : [organiserId, ...ids]
    // organiser first
    const ordered = [...withOrg].sort((a, b) => (a === organiserId ? -1 : b === organiserId ? 1 : 0))
    return ordered.map((id) => {
      const u = userById(id)
      return { id, name: nameOf(u), organiser: id === organiserId }
    })
  })()
  const attendeeCount = attendeeRows.length

  // Shared hero band (view + edit). `editable` swaps the title for an input.
  const heroBand = (editable: boolean): React.ReactNode => {
    const audienceView = loaded?.firmWide
      ? (<><span className="opacity-70">·</span><IcGlobe className="w-3.5 h-3.5" /><span>Firm-wide</span></>)
      : attendeeCount > 0
      ? (<><span className="opacity-70">·</span><IcUsers className="w-3.5 h-3.5" /><span>{attendeeCount} {attendeeCount === 1 ? 'attendee' : 'attendees'}</span></>)
      : null
    return (
      <div className="relative px-6 sm:px-8 py-6 text-white bg-gradient-to-br from-casevo-forest to-casevo-brand dark:from-[#1a4230] dark:to-[#2d6a4f]">
        <button type="button" onClick={requestClose} aria-label="Close"
          className="absolute top-4 right-4 w-8 h-8 rounded-lg bg-white/15 hover:bg-white/25 text-white flex items-center justify-center transition-colors focus:outline-none casevo-focus">✕</button>
        <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide px-2.5 py-1 rounded-md bg-white/20 mb-3.5">
          {typeIcon(type)}
          {editable ? (mode === 'edit' ? 'Edit appointment' : 'New appointment') : TYPE_LABEL[type]}
          {!editable && audienceView}
        </span>
        {editable ? (
          <>
            <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Appointment title…"
              className="w-full bg-white/10 border border-white/25 rounded-lg px-3 py-2 text-lg font-bold text-white placeholder-white/55 focus:outline-none focus:border-white/50" />
            <p className="text-xs text-white/70 mt-2">Fill in the details below · you'll be added as an attendee</p>
          </>
        ) : (
          <>
            <div className="text-[22px] font-bold leading-tight mb-4">{title}</div>
            {loaded && (
              <div className="flex flex-wrap gap-x-7 gap-y-3">
                <div className="flex items-center gap-2.5 text-sm">
                  <IcCalendar />
                  <span><span className="block text-[10px] uppercase tracking-wide opacity-70">Date</span><span className="font-semibold">{fmtDate(loaded.startAt)}</span></span>
                </div>
                <div className="flex items-center gap-2.5 text-sm">
                  <IcClock />
                  <span><span className="block text-[10px] uppercase tracking-wide opacity-70">Time</span><span className="font-semibold">{fmtTime(loaded.startAt)}{loaded.endAt ? ` – ${fmtTime(loaded.endAt)}` : ''}</span></span>
                </div>
                {location && (
                  <div className="flex items-center gap-2.5 text-sm">
                    <IcPin />
                    <span><span className="block text-[10px] uppercase tracking-wide opacity-70">Location</span><span className="font-semibold">{location}</span></span>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
    )
  }

  const activeUsers = firmUsers.filter((u) => u.isActive)

  return (
    <div className="fixed inset-0 z-[70] overflow-y-auto bg-black/40">
      {/* ★ NO LAYOUT SHIFT: editing an existing appointment loads it first. For
          the first 250ms only the backdrop shows (most loads finish by then, so
          the modal appears complete in one go); after that, a skeleton with the
          details layout — not a short "Loading…" the real view outgrew. */}
      {/* Vertically centered; a tall modal scrolls (min-h-full keeps the top
          reachable when content exceeds the viewport). */}
      {loadPhase !== 'wait' && (
      <div className="flex min-h-full items-center justify-center p-4">
      <div className="w-full max-w-4xl rounded-2xl overflow-hidden bg-casevo-surface dark:bg-[#18241d] shadow-2xl">
        {loading ? (
          <>
            {heroBand(false)}
            <div role="status" aria-label="Loading the appointment" className="px-6 sm:px-8 py-6 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-6 min-h-[300px]">
              {[0, 1].map((col) => (
                <div key={col} aria-hidden="true">
                  <span className={`${SKELETON_BAR} h-2.5 w-24 mb-4`} />
                  {[0, 1, 2].map((i) => (
                    <div key={i} className="mb-4">
                      <div className="text-[11px]"><span className={`${SKELETON_BAR} h-2 w-16`} /></div>
                      <div className="text-sm"><span className={`${SKELETON_BAR} h-3 w-44`} /></div>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </>
        ) : restricted && loaded ? (
          // ══════════════ RESTRICTED (Audit batch 3) ══════════════
          <>
            {heroBand(false)}
            <div className="px-6 sm:px-8 py-6 flex items-start gap-3 text-casevo-text-soft">
              <span className="w-[34px] h-[34px] rounded-[10px] bg-casevo-border-soft grid place-items-center shrink-0"><LockIcon className="w-4 h-4" /></span>
              <div>
                <p className="text-sm font-semibold text-casevo-forest">You can’t see this appointment’s details</p>
                <p className="text-sm mt-0.5">It’s on a case you don’t have access to, so only its time is shown.</p>
              </div>
            </div>
            <div className="px-6 sm:px-8 pb-6 flex justify-end">
              <button type="button" onClick={onClose}
                className="h-10 px-4 rounded-[10px] border border-casevo-border bg-casevo-surface text-sm font-medium text-casevo-forest hover:border-casevo-mid focus:outline-none casevo-focus">Close</button>
            </div>
          </>
        ) : view === 'view' && loaded ? (
          // ══════════════ VIEW MODE ══════════════
          <>
            {heroBand(false)}
            {error && <ErrorNote className="mx-6 sm:mx-8 mt-4">{error}</ErrorNote>}
            {(() => {
              // ★ ADAPTIVE VIEW: MANY attendees (> 4, and not firm-wide) → the
              // two-column split (Details+Notes left, attendee LIST + view-all
              // right). FEW attendees / firm-wide / just-you → a COMPACT layout
              // (Details + Attendees side-by-side on top, Notes full-width below)
              // so a sparse appointment never leaves a dead right column.
              const manyAttendees = !loaded.firmWide && attendeeCount > 4
              const detailsBlock = (
                <div>
                  <div className={bhCls}><IcFile />Details</div>
                  {caseId && (
                    <div className="mb-4">
                      <div className="text-[11.5px] font-semibold text-casevo-mid mb-1">Linked case</div>
                      {/* ★ Locked when the viewer can't access the case — no dead-end click. */}
                      <CaseLink caseId={caseId} onOpen={onOpenCase} lockedClassName="text-sm font-semibold"
                        className="text-sm font-semibold text-casevo-brand hover:text-casevo-forest hover:underline focus:outline-none casevo-focus">
                        Case · {caseShortId(caseId)}{caseLocked ? '' : ' →'}
                      </CaseLink>
                    </div>
                  )}
                  <div className="mb-1">
                    <div className="text-[11.5px] font-semibold text-casevo-mid mb-1">Location</div>
                    <div className="text-sm text-casevo-forest">{location || '—'}</div>
                  </div>
                </div>
              )
              const notesBlock = notes ? (
                <div>
                  <div className={bhCls}><IcNote />Notes</div>
                  <div className="bg-casevo-input dark:bg-[#1f2e26] border-l-[3px] border-casevo-light rounded-r-lg px-4 py-3 text-sm leading-relaxed text-casevo-forest whitespace-pre-wrap">{notes}</div>
                </div>
              ) : null
              // Attendees content, compact vs list depending on `asList`.
              const attendeesBlock = (asList: boolean): React.ReactNode => (
                <div>
                  <div className={bhCls}><IcUsers />{loaded.firmWide ? 'Firm-wide' : `Attendees · ${attendeeCount}`}</div>
                  {loaded.firmWide ? (
                    <div className="inline-flex items-center gap-2.5 border border-casevo-border dark:bg-[#1f2e26] rounded-lg px-3 py-2.5 text-sm text-casevo-forest">
                      <IcGlobe className="w-4 h-4 text-casevo-brand" />Everyone in the firm
                    </div>
                  ) : attendeeCount === 0 ? (
                    <div className="text-sm text-casevo-mid">Just you.</div>
                  ) : asList ? (
                    <>
                      <div className="flex flex-col gap-2">
                        {attendeeRows.slice(0, 4).map((a) => (
                          <div key={a.id} className="flex items-center gap-3 border border-casevo-border dark:bg-[#1f2e26] rounded-lg px-2.5 py-2">
                            <UserAvatar userId={a.id} fallbackName={a.name} size="rail" />
                            <span className="text-sm font-medium text-casevo-forest truncate">{a.name}</span>
                            {a.organiser && <span className="ml-auto text-[11px] text-casevo-mid shrink-0">Organiser</span>}
                          </div>
                        ))}
                      </div>
                      <button type="button" onClick={() => setAttendeesPopup(true)}
                        className="mt-2.5 w-full py-2 border border-dashed border-casevo-border rounded-lg bg-casevo-input dark:bg-[#1f2e26] text-casevo-brand text-xs font-semibold hover:border-casevo-mid focus:outline-none casevo-focus">
                        View all {attendeeCount} attendees
                      </button>
                    </>
                  ) : (
                    // Compact: small avatar+name chips, wrapping.
                    <div className="flex flex-wrap gap-2">
                      {attendeeRows.map((a) => (
                        <span key={a.id} className="inline-flex items-center gap-2 border border-casevo-border dark:bg-[#1f2e26] rounded-full pl-1 pr-3 py-1">
                          <UserAvatar userId={a.id} fallbackName={a.name} size="pill" />
                          <span className="text-[13px] font-medium text-casevo-forest">{a.name}</span>
                          {a.organiser && <span className="text-[10px] text-casevo-mid">· Organiser</span>}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              )
              return manyAttendees ? (
                // Two-column: Details+Notes left, attendee list right.
                <div className="px-6 sm:px-8 py-6 grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div className="flex flex-col gap-6">{detailsBlock}{notesBlock}</div>
                  {attendeesBlock(true)}
                </div>
              ) : (
                // Compact: Details + Attendees on top, Notes full-width below.
                <div className="px-6 sm:px-8 py-6">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
                    {detailsBlock}
                    {attendeesBlock(false)}
                  </div>
                  {notesBlock && <div className="mt-6">{notesBlock}</div>}
                </div>
              )
            })()}
            {/* "Last edited by" — only when someone other than the organiser
                edited last; nothing until the column is live (batch 2). */}
            {loaded && (
              <LastEditedLine
                className="px-6 sm:px-8 pb-3"
                editorId={loaded.lastEditedByUserId}
                editedAt={loaded.lastEditedAt}
                ownerId={loaded.createdByUserId}
              />
            )}
            {/* Footer */}
            <div className="flex items-center gap-2.5 px-6 sm:px-8 py-4 border-t border-casevo-border bg-casevo-input dark:bg-[#141f19]">
              {canModify ? (
                <>
                  {canDelete ? (
                    <button type="button" onClick={onDeleteClick} disabled={saving}
                      className="mr-auto px-4 py-2.5 rounded-lg border border-casevo-border text-sm font-semibold text-red-600 dark:text-red-300 hover:border-red-300 dark:hover:border-red-400/60 disabled:opacity-50 focus:outline-none casevo-focus">Delete</button>
                  ) : (
                    <span className="mr-auto" aria-hidden="true" />
                  )}
                  <button type="button" onClick={() => setView('edit')}
                    className={BTN_PRIMARY_MD}>Edit</button>
                </>
              ) : (
                <p className="mr-auto text-xs text-casevo-mid">View only — only the people involved or an admin can change this appointment.</p>
              )}
            </div>
          </>
        ) : (
          // ══════════════ EDIT / CREATE MODE ══════════════
          <>
            {heroBand(true)}
            {error && <ErrorNote className="mx-6 sm:mx-8 mt-4">{error}</ErrorNote>}
            <div className="px-6 sm:px-8 py-6 grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-0">
              {/* LEFT — core fields + recurrence + assignee */}
              <div>
                <div className="grid grid-cols-2 gap-3.5">
                  <div className="mb-4"><span className={lblCls}>Date</span><DateInput value={date || null} onChange={(v) => setDate(v ?? '')} allowFuture ariaLabel="Date" onValidityChange={(ok) => setDateTyping(!ok)} /></div>
                  <div className="mb-4"><label className={lblCls}>Type</label>
                    <select value={type} onChange={(e) => setType(e.target.value as AppointmentType)} className={inputCls}>
                      {TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                    </select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3.5">
                  <div className="mb-4"><label className={lblCls}>Start</label><input type="time" value={startTime} onChange={(e) => setStartTime(e.target.value)} className={inputCls} /></div>
                  <div className="mb-4"><label className={lblCls}>End</label><input type="time" value={endTime} onChange={(e) => setEndTime(e.target.value)} className={inputCls} /></div>
                </div>
                <div className="mb-4"><label className={lblCls}>Location</label><input value={location} onChange={(e) => setLocation(e.target.value)} className={inputCls} placeholder="Optional" /></div>
                <div className="mb-4">
                  <label className={lblCls}>Link case</label>
                  <div className="flex items-center gap-2">
                    <button type="button" onClick={() => setCasePickerOpen(true)}
                      className="flex-1 text-left border border-casevo-border rounded-lg px-3 py-2.5 text-sm bg-casevo-input text-casevo-forest hover:border-casevo-mid focus:outline-none casevo-focus">
                      {caseId ? (
                        <span className="inline-flex items-center gap-1.5" title={caseLocked ? LOCKED_CASE_TIP : undefined}>
                          {caseLocked && <LockIcon className="w-3.5 h-3.5 text-casevo-text-soft" />}
                          Case · {caseShortId(caseId)}
                          {caseLocked && <span className="text-casevo-text-soft text-xs">· no access</span>}
                        </span>
                      ) : 'Link a case (optional)'}
                    </button>
                    {caseId && <button type="button" onClick={() => { setCaseId(null); setClientId(null) }} className="text-xs text-casevo-mid hover:text-casevo-danger focus:outline-none casevo-focus">Clear</button>}
                  </div>
                  {mode === 'edit' && caseId && (
                    <div className="mt-1.5">
                      <CaseLink caseId={caseId} onOpen={onOpenCase} lockedClassName="text-xs"
                        className="text-xs text-casevo-brand hover:text-casevo-forest hover:underline focus:outline-none casevo-focus">
                        {caseLocked ? 'Case locked' : 'Open case →'}
                      </CaseLink>
                    </div>
                  )}
                </div>
                {/* Assignee (organiser/owner) — preserved. */}
                <div className="mb-4"><label className={lblCls}>Assignee</label>
                  <select value={assigneeId} onChange={(e) => setAssigneeId(e.target.value)} className={inputCls}>
                    <option value="">Unassigned</option>
                    {activeUsers.map((u) => <option key={u.id} value={u.id}>{nameOf(u)}</option>)}
                  </select>
                </div>
                {/* Recurrence (Repeats / Repeat on / Ends) — preserved. */}
                <div className="mb-4"><label className={lblCls}>Repeats</label>
                  <select value={freq ?? ''} onChange={(e) => {
                    const v = e.target.value; const next = v === '' ? null : (v as RecurrenceFreq); setFreq(next)
                    if ((next === 'weekly' || next === 'fortnightly') && byday.length === 0) { const wd = weekdayCodeForDate(date); if (wd) setByday([wd]) }
                  }} className={inputCls}>
                    <option value="">Does not repeat</option>
                    <option value="daily">Daily</option>
                    <option value="weekly">Weekly</option>
                    <option value="fortnightly">Every 2 weeks</option>
                    <option value="monthly">Monthly</option>
                  </select>
                </div>
                {(freq === 'weekly' || freq === 'fortnightly') && (
                  <div className="mb-4"><label className={lblCls}>Repeat on</label>
                    <div className="flex flex-wrap gap-1">
                      {WEEKDAYS.map((w) => {
                        const on = byday.includes(w.code)
                        return (
                          <button key={w.code} type="button" aria-pressed={on}
                            onClick={() => setByday((prev) => prev.includes(w.code) ? prev.filter((c) => c !== w.code) : [...prev, w.code])}
                            className={`px-2 py-1 rounded text-xs border transition-colors focus:outline-none casevo-focus ${on ? 'bg-casevo-primary text-white border-casevo-primary' : 'bg-casevo-input text-casevo-forest border-casevo-border hover:border-casevo-mid'}`}>{w.label}</button>
                        )
                      })}
                    </div>
                  </div>
                )}
                {freq && (
                  <div className="mb-4"><label className={lblCls}>Ends</label>
                    <div className="flex flex-col gap-2">
                      <select value={endsType} onChange={(e) => setEndsType(e.target.value as 'never' | 'on' | 'after')} className={inputCls}>
                        <option value="never">Never</option>
                        <option value="on">On date</option>
                        <option value="after">After N occurrences</option>
                      </select>
                      {endsType === 'on' && <DateInput value={endsDate || null} onChange={(v) => setEndsDate(v ?? '')} allowFuture ariaLabel="Repeat ends on" onValidityChange={(ok) => setEndsTyping(!ok)} />}
                      {endsType === 'after' && (
                        <div className="flex items-center gap-2">
                          <input type="number" min={1} value={endsCount} onChange={(e) => setEndsCount(Math.max(1, Number(e.target.value) || 1))} className={`${inputCls} !w-24`} />
                          <span className="text-xs text-casevo-mid">occurrences</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
              {/* RIGHT — audience (gated) + notes */}
              <div>
                {(canInviteAttendees || canCreateFirmWide) && (
                  <div className="mb-4">
                    <label className={lblCls}>Assign to</label>
                    <div className="flex rounded-lg border border-casevo-border overflow-hidden" role="group" aria-label="Appointment audience">
                      {([
                        { key: 'me' as const, label: 'Just me', show: true },
                        { key: 'people' as const, label: 'Specific people', show: canInviteAttendees },
                        { key: 'firm' as const, label: 'Everyone', show: canCreateFirmWide },
                      ]).filter((o) => o.show).map((o, i) => (
                        <button key={o.key} type="button" aria-pressed={audience === o.key}
                          onClick={() => { setAudience(o.key); if (o.key === 'people' && currentUserId && attendeeIds.length === 0) setAttendeeIds([currentUserId]) }}
                          className={`flex-1 py-2 text-xs font-semibold transition-colors focus:outline-none casevo-focus ${i > 0 ? 'border-l border-casevo-border' : ''} ${audience === o.key ? 'bg-casevo-primary text-white' : 'bg-casevo-surface text-casevo-forest hover:bg-casevo-mint/30'}`}>{o.label}</button>
                      ))}
                    </div>
                    {audience === 'firm' && <p className="mt-1.5 text-[11px] text-casevo-mid">Shows on everyone in the firm&apos;s calendar.</p>}
                    {audience === 'people' && (
                      <div className="mt-2">
                        <label className={lblCls}>People</label>
                        <div className="border border-casevo-border rounded-lg bg-casevo-input p-2 max-h-40 overflow-y-auto flex flex-col gap-0.5">
                          {activeUsers.map((u) => {
                            const checked = attendeeIds.includes(u.id)
                            const isYou = u.id === currentUserId
                            return (
                              <button key={u.id} type="button"
                                onClick={() => setAttendeeIds((prev) => checked ? prev.filter((id) => id !== u.id) : [...prev, u.id])}
                                className="flex items-center gap-2.5 px-2 py-1.5 rounded-md hover:bg-casevo-mint/40 focus:outline-none casevo-focus text-left">
                                <span className={`w-[17px] h-[17px] rounded-[5px] border flex items-center justify-center shrink-0 ${checked ? 'bg-casevo-primary border-casevo-primary text-white' : 'border-casevo-border'}`}>{checked && <IcCheck />}</span>
                                <UserAvatar userId={u.id} fallbackName={nameOf(u)} size="pill" />
                                <span className="text-[13px] text-casevo-forest truncate">{nameOf(u)}</span>
                                {isYou && <span className="ml-auto text-[10.5px] text-casevo-mid shrink-0">you</span>}
                              </button>
                            )
                          })}
                          {activeUsers.length === 0 && <span className="text-xs text-casevo-mid px-2 py-1">No firm members to invite.</span>}
                        </div>
                      </div>
                    )}
                  </div>
                )}
                <div className="mb-4"><label className={lblCls}>Notes</label>
                  <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={4} className={`${inputCls} resize-y min-h-[96px]`} placeholder="Optional" />
                </div>
              </div>
            </div>
            {/* Footer */}
            <div className="flex items-center justify-end gap-2.5 px-6 sm:px-8 py-4 border-t border-casevo-border bg-casevo-input dark:bg-[#141f19]">
              <button type="button" onClick={onCancelEdit} disabled={saving}
                className="px-5 py-2.5 rounded-lg border border-casevo-border text-sm font-semibold text-casevo-forest hover:border-casevo-mid disabled:opacity-50 focus:outline-none casevo-focus">Cancel</button>
              <button type="button" onClick={onSaveClick} disabled={saving}
                className={BTN_PRIMARY_MD}>
                {saving ? 'Saving…' : mode === 'edit' ? 'Save changes' : 'Create appointment'}
              </button>
            </div>
          </>
        )}
      </div>
      </div>
      )}

      {/* View-all attendees popup (big meetings). */}
      {attendeesPopup && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/40 px-4" onClick={() => setAttendeesPopup(false)}>
          <div className="w-full max-w-sm rounded-2xl overflow-hidden bg-casevo-surface dark:bg-[#18241d] shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center px-5 py-4 border-b border-casevo-border">
              <span className="text-base font-bold text-casevo-forest">Attendees</span>
              <span className="ml-auto text-xs text-casevo-mid">{attendeeCount} {attendeeCount === 1 ? 'person' : 'people'}</span>
            </div>
            <div className="px-5 py-4 max-h-80 overflow-y-auto flex flex-col gap-2">
              {attendeeRows.map((a) => (
                <div key={a.id} className="flex items-center gap-3 border border-casevo-border dark:bg-[#1f2e26] rounded-lg px-2.5 py-2">
                  <UserAvatar userId={a.id} fallbackName={a.name} size="rail" />
                  <span className="text-sm font-medium text-casevo-forest truncate">{a.name}</span>
                  {a.organiser && <span className="ml-auto text-[11px] text-casevo-mid shrink-0">Organiser</span>}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Recurring occurrence-vs-series this/all dialog. */}
      {scopePrompt && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/40 px-4">
          <div className="w-full max-w-xs rounded-lg border border-casevo-border bg-casevo-surface shadow-xl p-5">
            <h3 className="text-sm font-medium text-casevo-forest">{scopePrompt === 'save' ? 'Edit recurring appointment' : 'Delete recurring appointment'}</h3>
            <p className="mt-1 text-xs text-casevo-mid">{scopePrompt === 'save' ? 'Apply your changes to just this occurrence, or the whole series?' : 'Remove just this occurrence, or the whole series?'}</p>
            <div className="mt-4 flex flex-col gap-2">
              <button type="button" disabled={saving} onClick={() => { void (scopePrompt === 'save' ? handleSave('occurrence') : handleDelete('occurrence')) }}
                className="w-full text-left px-3 py-2 text-sm rounded border border-casevo-border text-casevo-forest bg-casevo-input hover:border-casevo-mid disabled:opacity-50 focus:outline-none casevo-focus">This occurrence only</button>
              <button type="button" disabled={saving} onClick={() => { void (scopePrompt === 'save' ? handleSave('series') : handleDelete('series')) }}
                className="w-full text-left px-3 py-2 text-sm rounded border border-casevo-border text-casevo-forest bg-casevo-input hover:border-casevo-mid disabled:opacity-50 focus:outline-none casevo-focus">{scopePrompt === 'save' ? 'All occurrences (whole series)' : 'The whole series'}</button>
              <button type="button" disabled={saving} onClick={() => setScopePrompt(null)}
                className="mt-1 text-xs uppercase tracking-widest text-casevo-brand hover:text-casevo-forest px-3 py-1.5 disabled:opacity-50 focus:outline-none casevo-focus">Cancel</button>
            </div>
          </div>
        </div>
      )}

      <LinkCasePickerModal isOpen={casePickerOpen} onClose={() => setCasePickerOpen(false)} currentCaseId="" excludeCaseIds={[]} title="Choose a case" actionLabel="Select case" onPick={(id) => { setCaseId(id); setCasePickerOpen(false) }} />
    </div>
  )
}

export default AppointmentFormModal
